import { fingerprint, type TuningState } from "@slime/contracts";
import type {
  CompiledRule,
  ContentCatalog,
  RuleRegistry,
} from "../engine/content/types";
import type { GameContext } from "../engine/core/GameContext";
import {
  compileCatalog,
  rawContentFromManifest,
  type RawContent,
} from "./compileCatalog";
import { applyOverlay, classify, locate, memberKey } from "./tuningPaths";

/** overlay 的持久化键。存 sessionStorage 而非 localStorage：关闭标签页即失效，避免几周后仍在生效而无人知晓。 */
export const OVERLAY_STORAGE_KEY = "slime.tuning.overlay";

export type OverlayStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
>;

/** 启动时带 overlay 编译失败的记录，供面板显示「上次的改动已被丢弃」。 */
export interface DiscardedOverlay {
  overlay: Record<string, number>;
  reason: string;
}

/** 只拷标量。数组与对象成员是结构性字段，规则参数另有按 instanceId 的拷回。 */
function copyScalars<T extends object>(
  live: readonly T[],
  next: readonly T[],
): void {
  for (let index = 0; index < live.length && index < next.length; index += 1) {
    const target = live[index] as Record<string, unknown>;
    const source = next[index] as unknown as Record<string, unknown>;
    for (const key of Object.keys(source)) {
      if (typeof source[key] === "number") target[key] = source[key];
    }
  }
}

/**
 * Rule 参数按 instanceId 拷回。重编译产生的是新 Rule 对象，而 RuleSystem 持有的是旧对象上的
 * `params`——不拷回，改 `core:leak-budget` 的 max 就永远不会被采用。
 */
/** 只拷数值，递归进嵌套对象；数组成员按 `memberKey` 配对（规则、波段、混投表），不按下标。 */
function copyNumbers(
  live: Record<string, unknown>,
  next: Record<string, unknown>,
): void {
  for (const key of Object.keys(next)) {
    const value = next[key];
    const current = live[key];
    if (typeof value === "number") live[key] = value;
    else if (Array.isArray(value) && Array.isArray(current)) {
      for (const item of value) {
        const key = memberKey(item);
        if (key === undefined) continue;
        const match = current.find(
          (candidate) => memberKey(candidate) === key,
        );
        if (match !== undefined)
          copyNumbers(
            match as Record<string, unknown>,
            item as Record<string, unknown>,
          );
      }
    } else if (
      value !== null &&
      typeof value === "object" &&
      current !== null &&
      typeof current === "object"
    ) {
      copyNumbers(
        current as Record<string, unknown>,
        value as Record<string, unknown>,
      );
    }
  }
}

function copyRuleParams(
  live: readonly CompiledRule[],
  next: readonly CompiledRule[],
): void {
  for (const rule of next) {
    const target = live.find(
      (candidate) => candidate.instanceId === rule.instanceId,
    );
    if (target === undefined) continue;
    copyNumbers(
      target.params as Record<string, unknown>,
      rule.params as Record<string, unknown>,
    );
  }
}

/**
 * 参数集的运行时形态（[决策 0017](../../../../docs/决策/0014-参数集作为带指纹的一等值.md)）。
 *
 * overlay 是唯一权威改动记录，生效方式是**写时应用**：叠加到原始 JSON 的克隆上，重跑
 * `compileCatalog`，再把结果就地拷回现有的 Compiled 定义对象。刻意复用同一个编译器而不是
 * 手写「路径 → 运行时字段」映射——后者迟早与编译器漂移，而调参台最不能出这个错。
 * 对象标识因此保持不变，`GameContext.catalog` 与各 Store 捕获的引用都无需改动，热循环零分支。
 *
 * **必须在 catalog 之前构造**：C 类字段走「重载生效」——overlay 持久化后整页重载，在编译之前
 * 叠加，于是每个启动期消费者拿到的本来就是新值，与手改 JSON 再刷新完全同一条路径。
 * 因此 catalog 由本类编译并持有（`catalog`），Game 从这里取而不是自己调 compileCatalog。
 */
export class TuningController {
  /** 磁盘内容。写回成功后更新，否则「这一局基于哪份内容」无法比对。 */
  private base = rawContentFromManifest();
  private readonly overlay = new Map<string, number>();
  /**
   * 运行中 catalog 实际编译自的原始内容。与 base 的差别是启动时叠加的 C 类条目；
   * 热改在它之上叠加，否则一条待重载的 C 类改动会被下一次热改顺手拷进运行时——正是决策
   * 0014 要避免的「物理认为世界宽 600、画布认为宽 480」。
   */
  private readonly runtimeRaw: RawContent;
  /** 本次会话碰过的 C 类路径。reloadRequired 只需比较这些。 */
  private readonly touchedReload = new Set<string>();
  private context?: GameContext;
  readonly catalog: ContentCatalog;
  readonly discarded?: DiscardedOverlay;
  private fingerprintCache: string;

  constructor(
    private readonly registry: RuleRegistry,
    private readonly storage?: OverlayStorage,
    private readonly onChange?: (
      path: string,
      from: number,
      to: number,
    ) => void,
  ) {
    this.fingerprintCache = fingerprint(this.base);
    const persisted = this.readPersisted();
    let runtime: RawContent | undefined;
    let catalog: ContentCatalog | undefined;
    if (persisted.size > 0) {
      // 安全网：带 overlay 编译失败会让游戏启动即崩，而那时面板尚未渲染，无法从界面清除它。
      // 因此丢弃 overlay、记明显警告并干净启动。
      const raw = structuredClone(this.base);
      const missing = applyOverlay(raw, persisted);
      try {
        if (missing.length > 0)
          throw new Error(`Unknown tuning path: ${missing.join(", ")}`);
        // 编译时用克隆：compileRules 会把 raw 里的 params 对象原样带进 CompiledRule，
        // 直接拿 runtimeRaw 编译会让「拷回 params」顺手改掉 runtimeRaw，reset 就再也回不到磁盘值。
        catalog = compileCatalog(registry, structuredClone(raw));
        runtime = raw;
        for (const [path, value] of persisted) this.overlay.set(path, value);
      } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        console.warn(
          `[tuning] Persisted overlay discarded, starting clean: ${reason}`,
          Object.fromEntries(persisted),
        );
        this.discarded = { overlay: Object.fromEntries(persisted), reason };
        this.storage?.removeItem(OVERLAY_STORAGE_KEY);
        catalog = undefined;
      }
    }
    if (runtime === undefined || catalog === undefined) {
      runtime = structuredClone(this.base);
      catalog = compileCatalog(registry, structuredClone(runtime));
    }
    this.runtimeRaw = runtime;
    this.catalog = catalog;
  }

  /** 只覆盖磁盘内容。overlay 变化不改变它。 */
  get baseFingerprint(): string {
    return this.fingerprintCache;
  }

  /** 绑定运行时上下文，供 B 类回填。context 依赖 catalog，因此只能在构造之后接上。 */
  bind(context: GameContext): void {
    this.context = context;
  }

  baseValue(path: string): number | undefined {
    const found = locate(this.base, path);
    return found === undefined ? undefined : (found.host[found.key] as number);
  }

  /** 当前生效值：overlay 优先，否则磁盘值。C 类路径的「生效」指重载后生效。 */
  effectiveValue(path: string): number | undefined {
    return this.overlay.get(path) ?? this.baseValue(path);
  }

  set(path: string, value: number): void {
    const base = this.baseValue(path);
    const kind = classify(path);
    if (base === undefined || kind === undefined)
      throw new Error(`Unknown tuning path: ${path}`);
    if (!Number.isFinite(value))
      throw new Error(`Tuning value must be finite: ${path}`);
    const from = this.overlay.get(path) ?? base;
    if (from === value) return;

    // 先在候选副本上算通再提交。内容 Schema 有边界约束（例如 fallDamping ≤ 1），越界值会
    // 被 compileCatalog 的校验拒绝；若先写 overlay 再 apply，失败后会留下一条从未生效的
    // 条目——state() 说「改了」而运行时说「没改」，且此后每次 apply 都重新撞上它。
    const candidate = new Map(this.overlay);
    // 改回磁盘原值等于没改，否则「overlay 为空 ⇔ 干净」会被一次来回拖动破坏。
    if (base === value) candidate.delete(path);
    else candidate.set(path, value);
    this.commit(candidate);
    if (kind === "reload") this.touchedReload.add(path);
    this.onChange?.(path, from, value);
  }

  reset(): void {
    if (this.overlay.size === 0) return;
    for (const path of this.overlay.keys())
      if (classify(path) === "reload") this.touchedReload.add(path);
    this.commit(new Map());
  }

  state(): TuningState {
    return {
      baseFingerprint: this.fingerprintCache,
      overlay: Object.fromEntries(this.overlay),
    };
  }

  /** 有 C 类改动尚未进入运行中的 catalog——它们要整页重载才生效。 */
  reloadRequired(): boolean {
    for (const path of this.touchedReload) {
      const running = locate(this.runtimeRaw, path);
      if (
        running !== undefined &&
        running.host[running.key] !== this.effectiveValue(path)
      )
        return true;
    }
    return false;
  }

  /**
   * 写回成功后调用：overlay 已提交进磁盘，base 随之更新、overlay 清空、指纹重算。
   * 运行中 catalog 不动——热改早已生效，C 类改动仍等重载，reloadRequired 因此照旧成立。
   */
  commitToBase(): void {
    if (this.overlay.size === 0) return;
    // 热改条目已在运行中生效，写进 runtimeRaw 才不会在下一次热改重编译时被退回旧值。
    applyOverlay(
      this.runtimeRaw,
      [...this.overlay].filter(([path]) => classify(path) === "hot"),
    );
    applyOverlay(this.base, this.overlay);
    this.overlay.clear();
    this.persist();
    this.fingerprintCache = fingerprint(this.base);
  }

  private readPersisted(): Map<string, number> {
    const result = new Map<string, number>();
    let text: string | null = null;
    try {
      text = this.storage?.getItem(OVERLAY_STORAGE_KEY) ?? null;
    } catch {
      return result;
    }
    if (text === null) return result;
    try {
      const parsed = JSON.parse(text) as unknown;
      if (
        parsed !== null &&
        typeof parsed === "object" &&
        !Array.isArray(parsed)
      ) {
        for (const [path, value] of Object.entries(parsed))
          if (typeof value === "number" && Number.isFinite(value))
            result.set(path, value);
      }
    } catch {
      /* 损坏的持久化内容等同于没有 overlay。 */
    }
    return result;
  }

  private persist(): void {
    try {
      if (this.overlay.size === 0)
        this.storage?.removeItem(OVERLAY_STORAGE_KEY);
      else
        this.storage?.setItem(
          OVERLAY_STORAGE_KEY,
          JSON.stringify(Object.fromEntries(this.overlay)),
        );
    } catch {
      /* 存储不可用时 overlay 只活在内存里，重载即丢；不因此阻断热改。 */
    }
  }

  /** 应用候选 overlay；抛错时当前状态原样不变。 */
  private commit(candidate: ReadonlyMap<string, number>): void {
    // 整份 overlay 必须能编译——它就是写回磁盘与重载时会用的那份内容。
    const full = structuredClone(this.base);
    applyOverlay(full, candidate);
    compileCatalog(this.registry, full);
    // 运行时只叠加热改条目：C 类条目留给重载。
    const runtime = structuredClone(this.runtimeRaw);
    applyOverlay(
      runtime,
      [...candidate].filter(([path]) => classify(path) === "hot"),
    );
    const next = compileCatalog(this.registry, runtime);
    // 编译成功才提交。
    this.overlay.clear();
    for (const [path, value] of candidate) this.overlay.set(path, value);
    this.persist();
    const live = this.catalog;
    copyScalars(live.slimes, next.slimes);
    copyScalars(live.drops, next.drops);
    copyScalars(
      live.levels.map((level) => level.world),
      next.levels.map((level) => level.world),
    );
    for (
      let index = 0;
      index < live.levels.length && index < next.levels.length;
      index += 1
    ) {
      const liveLevel = live.levels[index];
      const nextLevel = next.levels[index];
      copyRuleParams(liveLevel.world.rules, nextLevel.world.rules);
      // 关卡自己的规则散在各阶段数组里；世界规则与它们共享对象，重复拷一次无害。
      copyRuleParams(
        Object.values(liveLevel.rules).flat(),
        Object.values(nextLevel.rules).flat(),
      );
    }
    this.backfill();
  }

  /**
   * B 类回填（决策 0017）：只覆盖 spawn 时拷进 Store 的快照值。
   * O(活动实体数)，只在参数改动时发生，不进热循环。行为位不回填——增删能力会改变实体
   * 语义，只对新生成生效。`maxActiveRadius` 由 SpatialGridSystem 重建网格时刷新，晚于此处。
   */
  private backfill(): void {
    if (this.context === undefined) return;
    // 史莱姆半径自 T045 起是体积的函数，由压力机制每个固定步重新导出，不再是 spawn 快照，
    // 因此不在此回填——回填反而会覆盖掉当前充盈度对应的半径。
    const { drops } = this.context;
    // 水滴半径仍在 spawn 时被拷进 Store，且参与碰撞判定，不是纯表现。
    for (let id = 0; id < drops.capacity; id += 1) {
      if (drops.active[id] === 0) continue;
      drops.radius[id] = this.catalog.drops[drops.definition[id]].radius;
    }
  }
}
